import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Phone, Mail, X } from "lucide-react";

const PHONE_RAW = "0753221529";
const PHONE_INTL = "33753221529"; // for WhatsApp
const EMAIL = "depannagexpress67@gmail.com";
const DEFAULT_MSG = encodeURIComponent(
  "Bonjour D.E.S Dépannage, j'aimerais avoir des informations pour un devis."
);

export default function FloatingContact() {
  const [open, setOpen] = useState(false);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div data-testid="floating-contact" className="fixed bottom-5 right-5 sm:bottom-8 sm:right-8 z-[60]">
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="floating-contact-menu"
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-[72px] right-0 w-72 sm:w-80 bg-white rounded-2xl shadow-2xl border border-black/10 overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between bg-black text-white">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#FFB800]">Disponible 7j/7</div>
                <div className="font-display text-base font-medium tracking-tight">Comment souhaitez-vous nous joindre ?</div>
              </div>
              <button
                data-testid="floating-contact-close"
                onClick={() => setOpen(false)}
                aria-label="Fermer"
                className="text-neutral-400 hover:text-white transition"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-2">
              <ContactOption
                testId="contact-option-whatsapp"
                href={`https://wa.me/${PHONE_INTL}?text=${DEFAULT_MSG}`}
                target="_blank"
                rel="noopener noreferrer"
                title="WhatsApp"
                desc="Réponse rapide par message"
                color="#25D366"
                icon={<WhatsAppIcon />}
              />
              <ContactOption
                testId="contact-option-phone"
                href={`tel:${PHONE_RAW}`}
                title="Appeler"
                desc="07 53 22 15 29"
                color="#FF2800"
                icon={<Phone size={18} className="text-white" strokeWidth={2} />}
              />
              <ContactOption
                testId="contact-option-email"
                href={`mailto:${EMAIL}?subject=Demande%20de%20devis%20D.E.S%20D%C3%A9pannage`}
                title="Email"
                desc="depannagexpress67@gmail.com"
                color="#000000"
                icon={<Mail size={18} className="text-white" strokeWidth={2} />}
              />
            </div>

            <div className="px-5 py-3 bg-[#FAFAFA] border-t border-black/5 text-[11px] text-neutral-500 text-center uppercase tracking-[0.18em]">
              Devis gratuit · Sans engagement
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="floating-contact-button"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={open ? "Fermer le menu de contact" : "Ouvrir le menu de contact"}
        className="relative flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#25D366] text-white shadow-2xl hover:shadow-[#25D366]/40 transition-shadow"
      >
        {/* Pulse ring */}
        {!open && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-40 animate-ping" />
        )}
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={open ? "close" : "msg"}
            initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
            transition={{ duration: 0.2 }}
            className="relative flex items-center justify-center"
          >
            {open ? <X size={26} strokeWidth={2} /> : <WhatsAppIcon size={26} />}
          </motion.span>
        </AnimatePresence>

        {/* Notification dot */}
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#FF2800] border-2 border-white" />
        )}
      </motion.button>
    </div>
  );
}

function ContactOption({ href, target, rel, title, desc, color, icon, testId }) {
  return (
    <a
      data-testid={testId}
      href={href}
      target={target}
      rel={rel}
      className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#FAFAFA] transition group"
    >
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition group-hover:scale-105"
        style={{ background: color }}
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-black">{title}</div>
        <div className="text-xs text-neutral-500 truncate">{desc}</div>
      </div>
      <span className="text-neutral-300 group-hover:text-black transition">→</span>
    </a>
  );
}

function WhatsAppIcon({ size = 18 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="white" aria-hidden>
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.586-5.946C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.82 11.82 0 0 1 3.48 8.414c-.003 6.554-5.338 11.89-11.893 11.89a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
    </svg>
  );
}
